import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ensureValidSession } from "../services/authSession";
import {
  isOrderSurfacePath,
  ORDERS_SURFACE_ACTIVE_EVENT,
} from "../constants/orderSurfaces";

/**
 * Keeps auth + order list fresh on /orders and /orders-display (web kiosk surfaces).
 */
export function useOrdersPageSession(onRefresh) {
  const location = useLocation();
  const onRefreshRef = useRef(onRefresh);
  const isActiveSurface = isOrderSurfacePath(location.pathname);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const runRefresh = async () => {
    const ok = await ensureValidSession();
    if (ok) {
      onRefreshRef.current?.();
    }
    return ok;
  };

  useEffect(() => {
    if (!isActiveSurface) return;
    void runRefresh();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(ORDERS_SURFACE_ACTIVE_EVENT));
    }
  }, [isActiveSurface, location.pathname]);

  useEffect(() => {
    if (!isActiveSurface) return;

    const handleResume = () => {
      if (document.visibilityState && document.visibilityState !== "visible") {
        return;
      }
      void runRefresh();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(ORDERS_SURFACE_ACTIVE_EVENT));
      }
    };

    document.addEventListener("visibilitychange", handleResume);
    window.addEventListener("online", handleResume);
    window.addEventListener("focus", handleResume);

    return () => {
      document.removeEventListener("visibilitychange", handleResume);
      window.removeEventListener("online", handleResume);
      window.removeEventListener("focus", handleResume);
    };
  }, [isActiveSurface]);
}
